import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import type { EventInput } from '@creanizer/types'

interface EventFormProps {
  input: EventInput
  onChange: (value: EventInput) => void
}

export function EventForm({ input, onChange }: EventFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="event-type">イベントタイプ</Label>
        <Select
          value={input.eventType || 'encounter'}
          onValueChange={(value) => onChange({ ...input, eventType: value as EventInput['eventType'] })}
        >
          <SelectTrigger id="event-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="story">ストーリー進行</SelectItem>
            <SelectItem value="encounter">遭遇</SelectItem>
            <SelectItem value="exploration">探索</SelectItem>
            <SelectItem value="social">社交</SelectItem>
            <SelectItem value="puzzle">パズル</SelectItem>
            <SelectItem value="battle">戦闘</SelectItem>
            <SelectItem value="rest">休息</SelectItem>
            <SelectItem value="discovery">発見</SelectItem>
            <SelectItem value="crisis">危機</SelectItem>
            <SelectItem value="celebration">祝祭</SelectItem>
            <SelectItem value="mystery">謎</SelectItem>
            <SelectItem value="choice">選択</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {input.eventType === 'custom' && (
        <div>
          <Label htmlFor="custom-type">カスタムタイプ詳細</Label>
          <Input
            id="custom-type"
            value={input.customEventType || ''}
            onChange={(e) => onChange({ ...input, customEventType: e.target.value })}
            placeholder="独自のイベントタイプを入力..."
          />
        </div>
      )}

      <div>
        <Label htmlFor="genre">ジャンル</Label>
        <Select
          value={input.genre || 'fantasy'}
          onValueChange={(value) => onChange({ ...input, genre: value as EventInput['genre'] })}
        >
          <SelectTrigger id="genre">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fantasy">ファンタジー</SelectItem>
            <SelectItem value="sf">SF</SelectItem>
            <SelectItem value="modern">現代</SelectItem>
            <SelectItem value="post_apocalypse">ポストアポカリプス</SelectItem>
            <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
            <SelectItem value="steampunk">スチームパンク</SelectItem>
            <SelectItem value="mythology">神話</SelectItem>
            <SelectItem value="horror">ホラー</SelectItem>
            <SelectItem value="urban_fantasy">都市ファンタジー</SelectItem>
            <SelectItem value="custom">カスタム</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {input.genre === 'custom' && (
        <div>
          <Label htmlFor="custom-genre">カスタムジャンル詳細</Label>
          <Input
            id="custom-genre"
            value={input.customGenre || ''}
            onChange={(e) => onChange({ ...input, customGenre: e.target.value })}
            placeholder="独自のジャンルを入力..."
          />
        </div>
      )}

      <div>
        <Label htmlFor="difficulty">難易度</Label>
        <div className="space-y-2">
          <Slider
            id="difficulty"
            value={[input.difficulty || 5]}
            onValueChange={([value]) => onChange({ ...input, difficulty: value })}
            min={1}
            max={10}
            step={1}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>簡単</span>
            <span className="font-bold">{input.difficulty || 5}/10</span>
            <span>困難</span>
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="complexity">複雑度</Label>
        <Select
          value={input.complexity || 'moderate'}
          onValueChange={(value) => onChange({ ...input, complexity: value as EventInput['complexity'] })}
        >
          <SelectTrigger id="complexity">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">シンプル</SelectItem>
            <SelectItem value="moderate">標準</SelectItem>
            <SelectItem value="complex">複雑</SelectItem>
            <SelectItem value="very_complex">非常に複雑</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="duration">継続時間</Label>
        <Select
          value={input.duration || 'medium'}
          onValueChange={(value) => onChange({ ...input, duration: value as EventInput['duration'] })}
        >
          <SelectTrigger id="duration">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="short">短時間（30分以内）</SelectItem>
            <SelectItem value="medium">中程度（30分〜2時間）</SelectItem>
            <SelectItem value="long">長時間（2〜4時間）</SelectItem>
            <SelectItem value="epic">エピック（4時間以上）</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="player-count">推奨プレイヤー数</Label>
        <Input
          id="player-count"
          value={input.playerCount || ''}
          onChange={(e) => onChange({ ...input, playerCount: e.target.value })}
          placeholder="例: 3-5人"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="require-choices"
            checked={input.requireChoices || false}
            onCheckedChange={(checked) => onChange({ ...input, requireChoices: checked as boolean })}
          />
          <Label htmlFor="require-choices">選択肢・分岐を含む</Label>
        </div>

        {input.requireChoices && (
          <>
            <div>
              <Label htmlFor="number-choices">選択肢の数</Label>
              <Slider
                id="number-choices"
                value={[input.numberOfChoices || 3]}
                onValueChange={([value]) => onChange({ ...input, numberOfChoices: value })}
                min={2}
                max={10}
                step={1}
              />
              <div className="text-xs text-muted-foreground text-center mt-1">
                {input.numberOfChoices || 3}個
              </div>
            </div>

            <div>
              <Label htmlFor="choice-complexity">選択肢の複雑度</Label>
              <Select
                value={input.choiceComplexity || 'moderate'}
                onValueChange={(value) => onChange({ ...input, choiceComplexity: value as EventInput['choiceComplexity'] })}
              >
                <SelectTrigger id="choice-complexity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">シンプル（明確な違い）</SelectItem>
                  <SelectItem value="moderate">標準（複数の要素）</SelectItem>
                  <SelectItem value="complex">複雑（多面的な影響）</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="consequence-level">結果の重大性</Label>
              <Select
                value={input.consequenceLevel || 'moderate'}
                onValueChange={(value) => onChange({ ...input, consequenceLevel: value as EventInput['consequenceLevel'] })}
              >
                <SelectTrigger id="consequence-level">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">軽微</SelectItem>
                  <SelectItem value="moderate">中程度</SelectItem>
                  <SelectItem value="major">重大</SelectItem>
                  <SelectItem value="critical">致命的</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>

      <div>
        <Label htmlFor="mode">生成モード</Label>
        <Select
          value={input.mode || 'single'}
          onValueChange={(value) => onChange({ ...input, mode: value as EventInput['mode'] })}
        >
          <SelectTrigger id="mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">単体イベント</SelectItem>
            <SelectItem value="sequence">連続イベント</SelectItem>
            <SelectItem value="branch">分岐イベント</SelectItem>
            <SelectItem value="campaign">キャンペーン</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {input.mode === 'sequence' && (
        <>
          <div>
            <Label htmlFor="number-events">イベント数</Label>
            <Slider
              id="number-events"
              value={[input.numberOfEvents || 3]}
              onValueChange={([value]) => onChange({ ...input, numberOfEvents: value })}
              min={2}
              max={20}
              step={1}
            />
            <div className="text-xs text-muted-foreground text-center mt-1">
              {input.numberOfEvents || 3}個
            </div>
          </div>
          <div>
            <Label htmlFor="sequence-theme">連続テーマ</Label>
            <Input
              id="sequence-theme"
              value={input.sequenceTheme || ''}
              onChange={(e) => onChange({ ...input, sequenceTheme: e.target.value })}
              placeholder="例: 失われた遺跡の探索"
            />
          </div>
        </>
      )}

      {input.mode === 'branch' && (
        <div>
          <Label htmlFor="branching-depth">分岐の深さ</Label>
          <Slider
            id="branching-depth"
            value={[input.branchingDepth || 2]}
            onValueChange={([value]) => onChange({ ...input, branchingDepth: value })}
            min={1}
            max={5}
            step={1}
          />
          <div className="text-xs text-muted-foreground text-center mt-1">
            深さ {input.branchingDepth || 2}
          </div>
        </div>
      )}

      {input.mode === 'campaign' && (
        <div>
          <Label htmlFor="campaign-length">キャンペーン長さ</Label>
          <Select
            value={input.campaignLength || 'medium'}
            onValueChange={(value) => onChange({ ...input, campaignLength: value as EventInput['campaignLength'] })}
          >
            <SelectTrigger id="campaign-length">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="short">短期（5イベント）</SelectItem>
              <SelectItem value="medium">中期（10イベント）</SelectItem>
              <SelectItem value="long">長期（15イベント）</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="additional-settings">追加設定（オプション）</Label>
        <Textarea
          id="additional-settings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="イベントに関する追加の指示や設定..."
          rows={3}
        />
      </div>
    </div>
  )
}